<script setup>
    // Component
    import MainHeader from '@/components/HeaderComponent.vue';
    import Tooltip from '@/components/TooltipComponent.vue';
    import BaseSelect from '@/components/BaseSelectComponent.vue';
    import Utils from '@/commonProject/helpers/utils.js';
    import i18n from '@/common/i18n';

    // Store
    import { useCommonStore } from '@/stores/common/commonStore';
    import { useReserveStore } from '@/stores/reserve/reserveStore';
    import { useLanguageStore } from '@/stores/common/languageStore';

    // Vue
    import { onMounted, ref, computed, reactive, inject, watch } from 'vue';
    import { useRouter } from 'vue-router';
    import { getCurrentInstance } from 'vue';

    // メッセージダイアログ取得
    const { messageDialog } = inject('messageDialogKey');

    // ----------------------------
    // 定数
    // ----------------------------
    const router = useRouter();
    const commonStore = useCommonStore();
    const reserveStore = useReserveStore();
    const languageStore = useLanguageStore();
    const app = getCurrentInstance();
    const API_URL = app.appContext.config.globalProperties.$constProperties.BASE_API_URL_S;

    // ----------------------------
    // リアクティブ変数
    // ----------------------------
    const requestData = reactive(reserveStore.rsvdtlRequest);
    // ご相談内容1のリスト
    const firstIssue = ref([]);
    // ご相談内容2のリスト
    const secondIssue = ref([]);

    // ----------------------------
    // 関数
    // ----------------------------
    /**
     * 戻るボタン押下時
     */
    function onBack() {
        // 前画面からの戻るフラグを有効にする。
        commonStore.reservePageBack = true;

        // 遷移元予約画面のルートと、設定・選択状況に応じて、戻る画面を制御する。
        switch (commonStore.from) {
            case 'consultation':
                // 相談内容を選んで予約のルートは対象外
                break;
            case 'program':
                // プログラムを選んで予約のルート

                // 予約(規定同意)画面に戻る
                if (reserveStore.reserveStep.program.reserveplanningagree) {
                    router.push({ name: 'reserveplanningagree' });
                    return;
                }

                // 予約(プログラムからサービス選択)画面
                if (reserveStore.reserveStep.program.reserveplanningprogram) {
                    router.push({ name: 'reserveplanningprogram' });
                    return;
                }
                break;
            case 'personal':
                // 割当てられたプログラムを予約のルート

                // 予約(規定同意)画面に戻る
                if (reserveStore.reserveStep.personal.reserveplanningagree) {
                    router.push({ name: 'reserveplanningagree' });
                    return;
                }

                // 予約(プログラムからサービス選択)画面
                if (reserveStore.reserveStep.personal.reserveplanningpersonal) {
                    router.push({ name: 'reserveplanningpersonal' });
                    return;
                }
                break;
            default:
                // 戻り先指定無し。
                break;
        }
    }

    /**
     * 進むボタン押下時
     */
    function onAdvance() {
        // バリデーションチェック
        if (isValidationError()) {
            return;
        }
        reserveStore.setRsvdtlRequestData(requestData);

        // 予約(場所日時選択-カレンダー)画面および予約(場所日時選択-アレンジ)画面へ遷移する。
        router.push('/reserve/planning/counselor');
    }

    /**
     * ご相談内容1変化時
     */
    async function issueChange1() {
        // ご相談内容1選択済、ご利用方法・ご利用言語未選択の場合
        if (requestData.IssuePk1 !== '' && requestData.CngMethodType === '' && requestData.CngLanguagePk === '') {
            requestData.IssuePk2 = '';
            let issue1SelectedManagementRequestBody = {
                Lang: languageStore.getLanguageValue,
                //SelfUseMethod: '2',
                ServicePk: requestData.ServicePk,
                IssuePk1: requestData.IssuePk1,
            };
            // ホーム画面で選択した画面のルートに応じて、APIのリクエストボディに設定するパラメーターを制御する。
            switch (commonStore.getFrom) {
                case 'personal':
                    // 割当られたプログラムを予約のルート
                    issue1SelectedManagementRequestBody['SelfUseMethod'] = '0';
                    break;

                default:
                    // 上記以外の画面のルート
                    issue1SelectedManagementRequestBody['SelfUseMethod'] = '2';
                    break;
            }
            // 予約利用方法一覧API
            const response = await reserveStore.getManegmentAction(API_URL, issue1SelectedManagementRequestBody);
            if (!response.success) {
                messageDialog('error', response.errorMessage);
                if (response.redirect) {
                    location.href = response.redirectUrl;
                }
                return false;
            }
            // ご相談内容1・ご利用方法・ご利用言語選択済の場合
        } else if (requestData.IssuePk1 !== '' && requestData.CngMethodType !== '' && requestData.CngLanguagePk !== '') {
            requestData.IssuePk2 = '';
            let issue2SelectedManagementRequestBody = {
                Lang: languageStore.getLanguageValue,
                //SelfUseMethod: '2',
                ServicePk: requestData.ServicePk,
                IssuePk1: requestData.IssuePk1,
            };
            // ホーム画面で選択した画面のルートに応じて、APIのリクエストボディに設定するパラメーターを制御する。
            switch (commonStore.getFrom) {
                case 'personal':
                    // 割当られたプログラムを予約のルート
                    issue2SelectedManagementRequestBody['SelfUseMethod'] = '0';
                    break;

                default:
                    // 上記以外の画面のルート
                    issue2SelectedManagementRequestBody['SelfUseMethod'] = '2';
                    break;
            }
            // 予約利用方法一覧API
            const response = await reserveStore.getManegmentAction(API_URL, issue2SelectedManagementRequestBody);
            if (!response.success) {
                messageDialog('error', response.errorMessage);
                if (response.redirect) {
                    location.href = response.redirectUrl;
                }
                return false;
            } else {
                let languageRequestBody = {
                    Lang: languageStore.getLanguageValue,
                    //SelfUseMethod: '2',
                    ServicePk: requestData.ServicePk,
                    CngMethodType: requestData.CngMethodType,
                    IssuePk1: requestData.IssuePk1,
                };
                // ホーム画面で選択した画面のルートに応じて、APIのリクエストボディに設定するパラメーターを制御する。
                switch (commonStore.getFrom) {
                    case 'personal':
                        // 割当られたプログラムを予約のルート
                        languageRequestBody['SelfUseMethod'] = '0';
                        break;

                    default:
                        // 上記以外の画面のルート
                        languageRequestBody['SelfUseMethod'] = '2';
                        break;
                }
                // 予約利用言語一覧API
                const response = await reserveStore.getLanguageAction(API_URL, languageRequestBody);
                if (!response.success) {
                    messageDialog('error', response.errorMessage);
                    if (response.redirect) {
                        location.href = response.redirectUrl;
                    }
                    return false;
                }
            }
            // ご相談内容1・ご利用方法選択済、ご利用言語未選択の場合
        } else if (requestData.IssuePk1 !== '' && requestData.CngMethodType !== '' && requestData.CngLanguagePk === '') {
            requestData.IssuePk2 = '';
            let issue2SelectedManagementRequestBody = {
                Lang: languageStore.getLanguageValue,
                //SelfUseMethod: '2',
                ServicePk: requestData.ServicePk,
                IssuePk1: requestData.IssuePk1,
            };
            // ホーム画面で選択した画面のルートに応じて、APIのリクエストボディに設定するパラメーターを制御する。
            switch (commonStore.getFrom) {
                case 'personal':
                    // 割当られたプログラムを予約のルート
                    issue2SelectedManagementRequestBody['SelfUseMethod'] = '0';
                    break;

                default:
                    // 上記以外の画面のルート
                    issue2SelectedManagementRequestBody['SelfUseMethod'] = '2';
                    break;
            }
            // 予約利用方法一覧API
            const response = await reserveStore.getManegmentAction(API_URL, issue2SelectedManagementRequestBody);
            if (!response.success) {
                messageDialog('error', response.errorMessage);
                if (response.redirect) {
                    location.href = response.redirectUrl;
                }
                return false;
            } else {
                let languageRequestBody = {
                    Lang: languageStore.getLanguageValue,
                    //SelfUseMethod: '2',
                    ServicePk: requestData.ServicePk,
                    CngMethodType: requestData.CngMethodType,
                    IssuePk1: requestData.IssuePk1,
                };
                // ホーム画面で選択した画面のルートに応じて、APIのリクエストボディに設定するパラメーターを制御する。
                switch (commonStore.getFrom) {
                    case 'personal':
                        // 割当られたプログラムを予約のルート
                        languageRequestBody['SelfUseMethod'] = '0';
                        break;

                    default:
                        // 上記以外の画面のルート
                        languageRequestBody['SelfUseMethod'] = '2';
                        break;
                }
                // 予約利用言語一覧API
                const response = await reserveStore.getLanguageAction(API_URL, languageRequestBody);
                if (!response.success) {
                    messageDialog('error', response.errorMessage);
                    if (response.redirect) {
                        location.href = response.redirectUrl;
                    }
                    return false;
                }
            }
        } else {
            reset();
        }
    }

    /**
     * ご相談内容2変化時
     */
    async function issueChange2() {
        // ご相談内容1が選択済の場合
        if (requestData.IssuePk1 !== '') {
            // ご相談内容2が選択済の場合
            if (requestData.IssuePk2 !== '') {
                let issue2SelectedManagementRequestBody = {
                    Lang: languageStore.getLanguageValue,
                    //SelfUseMethod: '2',
                    ServicePk: requestData.ServicePk,
                    IssuePk1: requestData.IssuePk1,
                    IssuePk2: requestData.IssuePk2,
                };
                // ホーム画面で選択した画面のルートに応じて、APIのリクエストボディに設定するパラメーターを制御する。
                switch (commonStore.getFrom) {
                    case 'personal':
                        // 割当られたプログラムを予約のルート
                        issue2SelectedManagementRequestBody['SelfUseMethod'] = '0';
                        break;

                    default:
                        // 上記以外の画面のルート
                        issue2SelectedManagementRequestBody['SelfUseMethod'] = '2';
                        break;
                }
                // 予約利用方法一覧API
                await reserveStore.getManegmentAction(API_URL, issue2SelectedManagementRequestBody);

                // ご利用方法が選択済の場合
                if (requestData.CngMethodType !== '') {
                    let languageRequestBody = {
                        Lang: languageStore.getLanguageValue,
                        //SelfUseMethod: '2',
                        ServicePk: requestData.ServicePk,
                        CngMethodType: requestData.CngMethodType,
                        IssuePk1: requestData.IssuePk1,
                        IssuePk2: requestData.IssuePk2,
                    };
                    // ホーム画面で選択した画面のルートに応じて、APIのリクエストボディに設定するパラメーターを制御する。
                    switch (commonStore.getFrom) {
                        case 'personal':
                            // 割当られたプログラムを予約のルート
                            languageRequestBody['SelfUseMethod'] = '0';
                            break;

                        default:
                            // 上記以外の画面のルート
                            languageRequestBody['SelfUseMethod'] = '2';
                            break;
                    }
                    // 予約利用言語一覧API
                    const response = await reserveStore.getLanguageAction(API_URL, languageRequestBody);
                    if (!response.success) {
                        messageDialog('error', response.errorMessage);
                        if (response.redirect) {
                            location.href = response.redirectUrl;
                        }
                        return false;
                    }
                }
                // ご相談内容2が未選択の場合
            } else {
                let issue2SelectedManagementRequestBody = {
                    Lang: languageStore.getLanguageValue,
                    //SelfUseMethod: '2',
                    ServicePk: requestData.ServicePk,
                    IssuePk1: requestData.IssuePk1,
                };
                // ホーム画面で選択した画面のルートに応じて、APIのリクエストボディに設定するパラメーターを制御する。
                switch (commonStore.getFrom) {
                    case 'personal':
                        // 割当られたプログラムを予約のルート
                        issue2SelectedManagementRequestBody['SelfUseMethod'] = '0';
                        break;

                    default:
                        // 上記以外の画面のルート
                        issue2SelectedManagementRequestBody['SelfUseMethod'] = '2';
                        break;
                }
                // 予約利用方法一覧API
                const response = await reserveStore.getManegmentAction(API_URL, issue2SelectedManagementRequestBody);
                if (!response.success) {
                    messageDialog('error', response.errorMessage);
                    if (response.redirect) {
                        location.href = response.redirectUrl;
                    }
                    return false;
                } else {
                    let languageRequestBody = {
                        Lang: languageStore.getLanguageValue,
                        //SelfUseMethod: '2',
                        ServicePk: requestData.ServicePk,
                        CngMethodType: requestData.CngMethodType,
                        IssuePk1: requestData.IssuePk1,
                    };
                    // ホーム画面で選択した画面のルートに応じて、APIのリクエストボディに設定するパラメーターを制御する。
                    switch (commonStore.getFrom) {
                        case 'personal':
                            // 割当られたプログラムを予約のルート
                            languageRequestBody['SelfUseMethod'] = '0';
                            break;

                        default:
                            // 上記以外の画面のルート
                            languageRequestBody['SelfUseMethod'] = '2';
                            break;
                    }
                    // 予約利用言語一覧API
                    const response = await reserveStore.getLanguageAction(API_URL, languageRequestBody);
                    if (!response.success) {
                        messageDialog('error', response.errorMessage);
                        if (response.redirect) {
                            location.href = response.redirectUrl;
                        }
                        return false;
                    }
                }
            }
        }
    }

    /**
     * ご利用方法変化時
     */
    async function manegmentChange() {
        // ご相談内容1が選択済の場合
        if (requestData.IssuePk1 !== '') {
            // ご相談内容2選択済、ご利用言語未取得の場合
            if (requestData.IssuePk2 !== '' && reserveStore.languageList === '') {
                let languageRequestBody = {
                    Lang: languageStore.getLanguageValue,
                    //SelfUseMethod: '2',
                    ServicePk: requestData.ServicePk,
                    CngMethodType: requestData.CngMethodType,
                    IssuePk1: requestData.IssuePk1,
                    IssuePk2: requestData.IssuePk2,
                };
                // ホーム画面で選択した画面のルートに応じて、APIのリクエストボディに設定するパラメーターを制御する。
                switch (commonStore.getFrom) {
                    case 'personal':
                        // 割当られたプログラムを予約のルート
                        languageRequestBody['SelfUseMethod'] = '0';
                        break;

                    default:
                        // 上記以外の画面のルート
                        languageRequestBody['SelfUseMethod'] = '2';
                        break;
                }
                // 予約利用言語一覧API
                const response = await reserveStore.getLanguageAction(API_URL, languageRequestBody);
                if (!response.success) {
                    messageDialog('error', response.errorMessage);
                    if (response.redirect) {
                        location.href = response.redirectUrl;
                    }
                    return false;
                }
                // ご相談内容2が選択済の場合
            } else if (requestData.IssuePk2 !== '') {
                requestData.CngLanguagePk = '';
                let languageRequestBody = {
                    Lang: languageStore.getLanguageValue,
                    //SelfUseMethod: '2',
                    ServicePk: requestData.ServicePk,
                    CngMethodType: requestData.CngMethodType,
                    IssuePk1: requestData.IssuePk1,
                    IssuePk2: requestData.IssuePk2,
                };
                // ホーム画面で選択した画面のルートに応じて、APIのリクエストボディに設定するパラメーターを制御する。
                switch (commonStore.getFrom) {
                    case 'personal':
                        // 割当られたプログラムを予約のルート
                        languageRequestBody['SelfUseMethod'] = '0';
                        break;

                    default:
                        // 上記以外の画面のルート
                        languageRequestBody['SelfUseMethod'] = '2';
                        break;
                }
                // 予約利用言語一覧API
                const response = await reserveStore.getLanguageAction(API_URL, languageRequestBody);
                if (!response.success) {
                    messageDialog('error', response.errorMessage);
                    if (response.redirect) {
                        location.href = response.redirectUrl;
                    }
                    return false;
                }
                // ご相談内容2が未選択の場合
            } else {
                requestData.CngLanguagePk = '';
                let languageRequestBody = {
                    Lang: languageStore.getLanguageValue,
                    //SelfUseMethod: '2',
                    ServicePk: requestData.ServicePk,
                    CngMethodType: requestData.CngMethodType,
                    IssuePk1: requestData.IssuePk1,
                };
                // ホーム画面で選択した画面のルートに応じて、APIのリクエストボディに設定するパラメーターを制御する。
                switch (commonStore.getFrom) {
                    case 'personal':
                        // 割当られたプログラムを予約のルート
                        languageRequestBody['SelfUseMethod'] = '0';
                        break;

                    default:
                        // 上記以外の画面のルート
                        languageRequestBody['SelfUseMethod'] = '2';
                        break;
                }
                // 予約利用言語一覧API
                const response = await reserveStore.getLanguageAction(API_URL, languageRequestBody);
                if (!response.success) {
                    messageDialog('error', response.errorMessage);
                    if (response.redirect) {
                        location.href = response.redirectUrl;
                    }
                    return false;
                }
            }
        }
    }

    /**
     * バリデーションチェック
     */
    function isValidationError() {
        // エラーメッセージを初期化する。
        let message = '';

        // ご相談内容1
        if (Utils.isNullEmpty(requestData.IssuePk1)) {
            message += i18n.global.t('reservePlanningSelection.message.message1') + '\n';
        }
        // ご利用方法
        if (Utils.isNullEmpty(requestData.CngMethodType)) {
            message += i18n.global.t('reservePlanningSelection.message.message2') + '\n';
        }
        // ご利用言語
        if (Utils.isNullEmpty(requestData.CngLanguagePk)) {
            message += i18n.global.t('reservePlanningSelection.message.message3') + '\n';
        }

        // エラーメッセージが存在する場合は、以降の処理に進ませないので、バリデーションチェック判定を返却する。
        if (!Utils.isNullEmpty(message)) {
            // エラーメッセージが存在する場合は、画面に表示する。
            messageDialog('error', message);
            return true;
        }

        return false;
    }

    /**
     * 変数初期化処理
     */
    function reset() {
        // ご相談内容1の選択状況の初期化
        requestData.IssuePk1 = '';
        // ご相談内容2の選択状況の初期化
        requestData.IssuePk2 = '';
        // ご利用方法の選択項目の初期化
        reserveStore.cngMethodTypes = '';
        requestData.CngMethodType = '';
        // ご利用言語の選択項目の初期化
        reserveStore.languageList = '';
        requestData.CngLanguagePk = '';
    }

    // ----------------------------
    // 算術プロパティ
    // ----------------------------
    // ご相談内容1の内容を消す処理
    const filteredIssues = computed(() => {
        return firstIssue.value.filter((issue) => issue.pk !== requestData.IssuePk1);
    });

    watch(filteredIssues, (secondFilteredIssue) => {
        secondIssue.value = secondFilteredIssue;
    });

    // ----------------------------
    // ライフサイクル
    // ----------------------------
    onMounted(async () => {
        // 前画面から戻ってきたか判定する。
        if (!commonStore.reservePageBack) {
            // 前画面から戻ってきていない場合は、本画面を初期化する。
            reset();
        }

        // 前画面からの戻るフラグを初期化する。
        commonStore.reservePageBack = false;

        let requestBody = {
            Lang: languageStore.getLanguageValue,
            //SelfUseMethod: '2',
            ServicePk: requestData.ServicePk,
        };

        // ホーム画面で選択した画面のルートに応じて、APIのリクエストボディに設定するパラメーターを制御する。
        switch (commonStore.getFrom) {
            case 'personal':
                // 割当られたプログラムを予約のルート
                requestBody['SelfUseMethod'] = '0';
                break;

            default:
                // 上記以外の画面のルート
                requestBody['SelfUseMethod'] = '2';
                break;
        }

        // 予約イシュー一覧取得API
        const response = await reserveStore.getIssuesAction(API_URL, requestBody);

        if (!response.success) {
            messageDialog('error', response.errorMessage);
            if (response.redirect) {
                location.href = response.redirectUrl;
            }
            return false;
        } else {
            if (!Utils.isNullEmptyArray(reserveStore.issueList)) {
                // 相談内容が1件の場合のみ、本処理を実行する。
                if (reserveStore.issueList.length === 1) {
                    // 相談内容（イシュー）が1件しかないので、唯一のデータのpkフィールドのみを取得する。
                    requestData.IssuePk1 = reserveStore.issueList[0].pk;

                    // 予約利用方法一覧APIのリクエストボディを生成する。
                    let requestBody = {
                        Lang: languageStore.getLanguageValue,
                        ServicePk: requestData.ServicePk,
                        IssuePk1: requestData.IssuePk1,
                    };

                    // ホーム画面で選択した画面のルートに応じて、APIのリクエストボディに設定するパラメーターを制御する。
                    switch (commonStore.getFrom) {
                        case 'personal':
                            // 割当られたプログラムを予約のルート
                            requestBody['SelfUseMethod'] = '0';
                            break;

                        default:
                            // 上記以外の画面のルート
                            requestBody['SelfUseMethod'] = '2';
                            break;
                    }

                    // 予約利用方法一覧APIをコールする。
                    const response = await reserveStore.getManegmentAction(API_URL, requestBody);
                    if (!response.success) {
                        messageDialog('error', response.errorMessage);
                        if (response.redirect) {
                            location.href = response.redirectUrl;
                        }
                        return false;
                    } else {
                        if (!Utils.isNullEmptyArray(reserveStore.cngMethodTypes)) {
                            // 利用方法が1件の場合
                            if (reserveStore.cngMethodTypes.length === 1) {
                                requestData.CngMethodType = reserveStore.cngMethodTypes[0];
                                let requestBody = {
                                    Lang: languageStore.getLanguageValue,
                                    //SelfUseMethod: '2',
                                    ServicePk: requestData.ServicePk,
                                    CngMethodType: requestData.CngMethodType,
                                    IssuePk1: requestData.IssuePk1,
                                };
                                // ホーム画面で選択した画面のルートに応じて、APIのリクエストボディに設定するパラメーターを制御する。
                                switch (commonStore.getFrom) {
                                    case 'personal':
                                        // 割当られたプログラムを予約のルート
                                        requestBody['SelfUseMethod'] = '0';
                                        break;

                                    default:
                                        // 上記以外の画面のルート
                                        requestBody['SelfUseMethod'] = '2';
                                        break;
                                }
                                // 予約利用言語一覧API
                                const response = await reserveStore.getLanguageAction(API_URL, requestBody);
                                if (!response.success) {
                                    messageDialog('error', response.errorMessage);
                                    if (response.redirect) {
                                        location.href = response.redirectUrl;
                                    }
                                    return false;
                                } else {
                                    if (!Utils.isNullEmptyArray(reserveStore.languageList)) {
                                        // 利用言語が1件の場合
                                        if (reserveStore.languageList.length === 1) {
                                            requestData.CngLanguagePk = reserveStore.languageList[0].pk;
                                            reserveStore.setRsvdtlRequestData(requestData);
                                            // 予約(場所日時選択-カレンダー)画面および予約(場所日時選択-アレンジ)画面へ遷移する。
                                            router.push({ name: 'reserveplanningcounselor' });
                                        }
                                    }
                                }
                            }
                        }
                    }
                }
            }
        }
        // イシュー・利用方法・利用言語いずれかが複数ある場合、座標を記録。すべて１件の場合は本画面はスキップされるため、記録しない。
        if (!(reserveStore.issueList.length === 1 && reserveStore.cngMethodTypes.length === 1 && reserveStore.languageList.length === 1)) {
            // 戻り先座標を記録する。
            // 遷移元予約画面のルートと、設定・選択状況に応じて、戻る画面を制御する。
            switch (commonStore.from) {
                case 'consultation':
                    // 相談内容を選んで予約のルートは対象外
                    break;
                case 'program':
                    // プログラムを選んで予約のルート
                    reserveStore.reserveStep.program.reserveplanningselection = true;
                    break;
                case 'personal':
                    // 割当てられたプログラムを予約のルート
                    reserveStore.reserveStep.personal.reserveplanningselection = true;
                    break;
            }
        }

        firstIssue.value = reserveStore.issueList;
        secondIssue.value = [];
    });
</script>

<template>
    <section id="main">
        <MainHeader />
        <div id="contents">
            <div class="whiteBox">
                <h3 class="reserve">
                    <span>{{ $t('reservePlanningSelection.parts.pageTitle1') }}</span>
                    <!-- ステップ表示 -->
                    <template v-if="languageStore.getLanguageValue === '1'">
                        <img src="/img/step/mode3_step3.png" class="step" width="410" height="46" />
                    </template>
                    <template v-else>
                        <img src="/img/step/mode3_step3_en.png" class="step" width="410" height="46" />
                    </template>
                </h3>
                <section class="whiteBoxContents scroll">
                    <!-- ステップ表示 -->
                    <template v-if="languageStore.getLanguageValue === '1'">
                        <img src="/img/step/mode3_step3.png" class="step" width="410" height="46" />
                    </template>
                    <template v-else>
                        <img src="/img/step/mode3_step3_en.png" class="step" width="410" height="46" />
                    </template>
                    <section class="form">
                        <dl>
                            <dt>
                                <ul class="formLabel">
                                    <li>
                                        <label>{{ $t('reservePlanningSelection.parts.dl1') }}</label>
                                    </li>
                                    <li class="must">
                                        <span>{{ $t('common.parts.must') }}</span>
                                    </li>
                                </ul>
                            </dt>
                            <dd>
                                <ul class="formInput spKeepFlex">
                                    <li>
                                        <BaseSelect :options="firstIssue" v-model="requestData.IssuePk1" required @childEmit="issueChange1" />
                                    </li>
                                </ul>
                            </dd>
                        </dl>
                        <dl>
                            <dt>
                                <ul class="formLabel">
                                    <li>
                                        <label>{{ $t('reservePlanningSelection.parts.dl2') }}</label>
                                    </li>
                                </ul>
                            </dt>
                            <dd>
                                <ul class="formInput spKeepFlex">
                                    <li>
                                        <BaseSelect :options="secondIssue" v-model="requestData.IssuePk2" required @childEmit="issueChange2" />
                                    </li>
                                </ul>
                            </dd>
                        </dl>
                        <dl>
                            <dt>
                                <ul class="formLabel">
                                    <li>
                                        <label>{{ $t('reservePlanningSelection.parts.dl3') }}</label>
                                    </li>
                                    <li>
                                        <Tooltip>
                                            {{ $t('reservePlanningSelection.parts.blowoutInformation1') }}<br />
                                            {{ $t('reservePlanningSelection.parts.blowoutInformation2') }}<br />
                                            {{ $t('reservePlanningSelection.parts.blowoutInformation3') }}
                                        </Tooltip>
                                    </li>
                                </ul>
                            </dt>
                            <dd>
                                <ul class="formInput">
                                    <li>
                                        <ul class="innerBtnArea radioBtnGroup">
                                            <li :class="'type' + cngMethodType" v-for="cngMethodType in reserveStore.cngMethodTypes" :key="cngMethodType">
                                                <input type="radio" :id="'radioBtn' + cngMethodType" name="radioBtnGroup" :value="cngMethodType" :checked="requestData.CngMethodType === cngMethodType" v-model="requestData.CngMethodType" @change="manegmentChange()" />
                                                <label class="radioBtn" :for="'radioBtn' + cngMethodType">{{ commonStore.getConvertDictionary('CngMethodType', cngMethodType, languageStore.getLanguageValue) }}</label>
                                            </li>
                                        </ul>
                                    </li>
                                </ul>
                            </dd>
                        </dl>
                        <dl>
                            <dt>
                                <ul class="formLabel">
                                    <li>
                                        <label>{{ $t('reservePlanningSelection.parts.dl4') }}</label>
                                    </li>
                                    <li>
                                        <Tooltip>{{ $t('reservePlanningSelection.parts.blowoutInformation4') }}</Tooltip>
                                    </li>
                                </ul>
                            </dt>
                            <dd>
                                <ul class="formInput">
                                    <li>
                                        <BaseSelect :options="reserveStore.languageList" v-model="requestData.CngLanguagePk" required />
                                    </li>
                                </ul>
                            </dd>
                        </dl>
                    </section>
                </section>
                <section class="whiteBoxFooter">
                    <ul class="btnArea center">
                        <li>
                            <button type="button" class="btnNormal" @click="onBack">{{ $t('common.parts.backPage') }}</button>
                        </li>
                        <li>
                            <button type="submit" class="btnPrimary" @click="onAdvance">{{ $t('common.parts.nextPage') }}</button>
                        </li>
                    </ul>
                </section>
            </div>
        </div>
    </section>
</template>

<style></style>
